import React from 'react';
import ConnectedAlternateAddress from 'components/Form/Location/AlternateAddress';
import {
  HistoryEmploymentItemModel,
  EmploymentActivityModel,
  SupervisorModel,
  DismissalModel,
} from 'forms/sections/history/employment/types';
import ValidationElement, {
  ValidationElementProps,
} from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate,
  CommonOnError,
  CommonScrollIntoView,
  CommonRequired,
  CommonAddressBooks,
} from 'types/props';
import { Dispatch } from 'redux';
import { FormTypes } from 'constants/formTypes';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import Show from 'components/Form/Show';
import Field from 'components/Form/Field';
import DateRange from 'components/Form/DateRange';
import Telephone from 'components/Form/Telephone';
import Name from 'components/Form/Name';
import Location from 'components/Form/Location';
import Text from 'components/Form/Text';
import Comments from 'components/Form/Comments';
import PVQLocation from 'components/Form/PVQLocation';
import LocationUnknown from 'components/Form/PVQLocation/LocationUnknown';
import Branch from 'components/Form/Branch';
import Dropdown from 'components/Form/Dropdown';
import NotApplicable from 'components/Form/NotApplicable';
import FederalAgencyAutoComplete from 'components/Form/FederalAgencyAutoComplete';
import { isPVQForm } from 'helpers/form';
import {
  employmentActivityTypes, employmentDimissalTypes, employmentMilitaryBranchValues, employmentModel
} from 'constants/enums/employmentOptions';
import DismissalItem from 'components/Form/PVQEmployment/DismissalItem';
import { YesOrNoModel } from 'forms/shared/types';
import PvqReprimand from 'components/Form/PVQEmployment/Reprimand';
import { i18n } from 'config/locales';
import RadioGroup from 'components/Form/RadioGroup';
import Radio from 'components/Form/Radio';
import { extractDate } from '../dateranges';
import Reprimand from './Reprimand';
import ReasonLeft from './ReasonLeft';
import Supervisor from './Supervisor';
import AdditionalActivity from './AdditionalActivity';
import PhysicalAddress from './PhysicalAddress';
import EmploymentStatus from './EmploymentStatus';
import EmploymentActivity from './EmploymentActivity';
import Assignments from './Assignment';
import Verifier from './Verifier';

const selfEmploytTitles = {
  main: 'history.pvq.selfEmploymentTitles.main',
  emails: 'history.pvq.selfEmploymentTitles.emails',
  emailsAppend: 'history.pvq.selfEmploymentTitles.emailsAppend',
  emailsEmailFieldTitle: 'history.pvq.selfEmploymentTitles.emailsEmailFieldTitle',
  telephones: 'history.pvq.selfEmploymentTitles.telephones',
  telephonesAppend: 'history.pvq.selfEmploymentTitles.telephonesAppend',
  details: 'history.pvq.selfEmploymentTitles.details',
  addressLabel: 'history.pvq.selfEmploymentTitles.addressLabel',
  domestic: 'history.pvq.selfEmploymentTitles.domestic',
  foreign: 'history.pvq.selfEmploymentTitles.foreign',
};

const unEmployTitles = {
  main: 'history.pvq.unemploymentTitles.main',
  emails: 'history.pvq.unemploymentTitles.emails',
  emailsAppend: 'history.pvq.unemploymentTitles.emailsAppend',
  emailsEmailFieldTitle: 'history.pvq.unemploymentTitles.emailsEmailFieldTitle',
  telephones: 'history.pvq.unemploymentTitles.telephones',
  telephonesAppend: 'history.pvq.unemploymentTitles.telephonesAppend',
  details: 'history.pvq.unemploymentTitles.details',
  addressLabel: 'history.pvq.unemploymentTitles.addressLabel',
  domestic: 'history.pvq.unemploymentTitles.domestic',
  foreign: 'history.pvq.unemploymentTitles.foreign',
};

interface Props extends ValidationElementProps, Partial<HistoryEmploymentItemModel> {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  recordYears?: number
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  addressBooks?: CommonAddressBooks

  // TODO: Remove.
  dispatch?: Dispatch

  formType?: FormTypes

  requireEmploymentItemStatus?: boolean
  requireEmploymentItemLeft?: boolean
  requireEmploymentItemNonGovernment?: boolean
  requireEmploymentHistorySupervisorEmail?: boolean
  requireSupervisorHasDifferentAddress?: boolean
  requireSupervisorAddress?: boolean
  requireEmploymentSelfEmployedJobLocation?: boolean
}

export default class EmploymentItem extends ValidationElement<Props> {
  constructor(props) {
    super(props);

    this.updateSupervisorAlternateAddressAnswerComments = this.updateSupervisorAlternateAddressAnswerComments.bind(this);
    this.updateSupervisorAlternateAddressPhysicalComments = this.updateSupervisorAlternateAddressPhysicalComments.bind(this);
    this.updateSupervisorAlternateAddressComments = this.updateSupervisorAlternateAddressComments.bind(this);
    this.updateSupervisorComments = this.updateSupervisorComments.bind(this);
    this.updateReprimandAnswerComments = this.updateReprimandAnswerComments.bind(this);
    this.updateReprimandComments = this.updateReprimandComments.bind(this);
    this.updateAddressAnswerComments = this.updateAddressAnswerComments.bind(this);
    this.updateAddressPhysicalComments = this.updateAddressPhysicalComments.bind(this);
    this.updateAddressComments = this.updateAddressComments.bind(this);
    this.updateReferenceAlternateAddressAnswerComments = this.updateReferenceAlternateAddressAnswerComments.bind(this);
    this.updateReferenceAlternateAddressPhysicalComments = this.updateReferenceAlternateAddressPhysicalComments.bind(this);
    this.updateReferenceAlternateAddressComments = this.updateReferenceAlternateAddressComments.bind(this);
    this.updateReasonLeftComments = this.updateReasonLeftComments.bind(this);
    this.updateReasonLeftAnswerComments = this.updateReasonLeftAnswerComments.bind(this);
    this.updatePhysicalAddressAnswerComments = this.updatePhysicalAddressAnswerComments.bind(this);
    this.updatePhysicalAddressComments = this.updatePhysicalAddressComments.bind(this);
  }

  handleNameError = (value: any, arr: ErrorCode[]) => {
    const arr2 = arr.map((err) => ({
      ...err,
      code: err.code.includes('required')
        ? `name${err.code.substring(err.code.lastIndexOf('.'))}`
        : err.code,
    }));
    return this.props.onError(value, arr2);
  };

  componentDidMount() {
    this.shouldClearActivity();
  }

  shouldClearActivity() {
    const showAdditional = this.showAdditionalActivity();

    if (!showAdditional && this.props.Additional?.items?.length > 0) {
      this.update({ Additional: {} });
    }
  }

  // #region Controller

  update = (queue: any) => {
    this.props.onUpdate({
      EmploymentActivity: this.props.EmploymentActivity,
      Employer: this.props.Employer,
      Dates: this.props.Dates,
      Title: this.props.Title,
      DutyStation: this.props.DutyStation,
      Status: this.props.Status,
      Address: this.props.Address,
      Telephone: this.props.Telephone,
      Supervisor: this.props.Supervisor,
      ReferenceName: this.props.ReferenceName,
      ReferencePhone: this.props.ReferencePhone,
      ReferenceAddress: this.props.ReferenceAddress,
      PhysicalAddress: this.props.PhysicalAddress,
      Additional: this.props.Additional,
      ReasonLeft: this.props.ReasonLeft,
      Reprimand: this.props.Reprimand,
      AlternateAddress: this.props.AlternateAddress,
      PhysicalAlternateAddress: this.props.PhysicalAlternateAddress,
      ReferenceAlternateAddress: this.props.ReferenceAlternateAddress,
      SupervisorAlternateAddress: this.props.SupervisorAlternateAddress,
      EmploymentActivityComments: this.props.EmploymentActivityComments,
      MilitaryEmploymentComments: this.props.MilitaryEmploymentComments,
      NonMilitaryEmploymentComments: this.props.NonMilitaryEmploymentComments,
      Comments: this.props.Comments,
      PhysicalAddressAnswerComments: this.props.PhysicalAddressAnswerComments,
      PhysicalAddressComments: this.props.PhysicalAddressComments,
      SupervisorAlternateAddressPhysicalComments: this.props.SupervisorAlternateAddressPhysicalComments,
      SupervisorAlternateAddressAnswerComments: this.props.SupervisorAlternateAddressAnswerComments,
      SupervisorAlternateAddressComments: this.props.SupervisorAlternateAddressComments,
      SelfEmploymentComments: this.props.SelfEmploymentComments,
      AddressPhysicalComments: this.props.AddressPhysicalComments,
      AddressAnswerComments: this.props.AddressAnswerComments,
      AddressComments: this.props.AddressComments,
      ReprimandComments: this.props.ReprimandComments,
      ReprimandAnswerComments: this.props.ReprimandAnswerComments,
      ReasonLeftComments: this.props.ReasonLeftComments,
      ReasonLeftAnswerComments: this.props.ReasonLeftAnswerComments,
      ReferenceComments: this.props.ReferenceComments,
      ReferenceAlternateAddressPhysicalComments: this.props.ReferenceAlternateAddressPhysicalComments,
      ReferenceAlternateAddressAnswerComments: this.props.ReferenceAlternateAddressAnswerComments,
      ReferenceAlternateAddressComments: this.props.ReferenceAlternateAddressComments,
      UnemploymentComments: this.props.UnemploymentComments,
      IsInternship: this.props.IsInternship,
      MilitaryBranch: this.props.MilitaryBranch,
      Assignments: this.props.Assignments,
      WasFired: this.props.WasFired,
      FiredDetails: this.props.FiredDetails,
      WasLeaveBeforeFired: this.props.WasLeaveBeforeFired,
      LeaveBeforeFiredDetails: this.props.LeaveBeforeFiredDetails,
      WasLeaveAfterNoticeMisconduct: this.props.WasLeaveAfterNoticeMisconduct,
      LeaveAfterNoticeMisconductDetails: this.props.LeaveAfterNoticeMisconductDetails,
      WasLeaveAfterNoticePerformance: this.props.WasLeaveAfterNoticePerformance,
      LeaveAfterNoticePerformanceDetails: this.props.LeaveAfterNoticePerformanceDetails,
      WasLeavePendingOutcome: this.props.WasLeavePendingOutcome,
      LeavePendingOutcomeDetails: this.props.LeavePendingOutcomeDetails,
      HasDifferentPhysicalWorkAddress: this.props.HasDifferentPhysicalWorkAddress,
      PhysicalWorkAddress: this.props.PhysicalWorkAddress,
      StillInBusiness: this.props.StillInBusiness,
      ReturnedToWork: this.props.ReturnedToWork,
      ReturnDate: this.props.ReturnDate,
      HasAssociatedCompany: this.props.HasAssociatedCompany,
      SelfEmployedEmail: this.props.SelfEmployedEmail,
      SelfEmployedEmailNotApplicable: this.props.SelfEmployedEmailNotApplicable,
      SelfEmployedWebsite: this.props.SelfEmployedWebsite,
      SelfEmployedWebsiteNotApplicable: this.props.SelfEmployedWebsiteNotApplicable,
      IsHomeCompany: this.props.IsHomeCompany,
      ...queue,
    });
  };

  getStatusUpdate = (incomingActivity: EmploymentActivityModel) => (
    this.showStatus(incomingActivity) ? {} : employmentModel.status
  );

  getReferenceUpdate = (incomingActivity: EmploymentActivityModel) => (
    this.showReference(incomingActivity) ? {} : employmentModel.reference
  );

  getEmployerUpdate = (incomingActivity: EmploymentActivityModel) => (
    this.showEmployer(incomingActivity) ? {} : employmentModel.employer
  );

  getSelfEmploymentUpdate = (incomingActivity: EmploymentActivityModel) => (
    this.showSelfEmployment(incomingActivity) ? {} : employmentModel.selfEmployment
  );

  getUnemploymentUpdate = (incomingActivity: EmploymentActivityModel) => (
    this.showUnemployment(incomingActivity) ? {} : employmentModel.unemployment
  );

  getAdditionalActivityUpdate = (incomingActivity: EmploymentActivityModel) => (
    this.showAdditionalActivity(incomingActivity) ? {} : employmentModel.additionalActivity
  );

  getAssignedDutyUpdate = (incomingActivity: EmploymentActivityModel) => (
    this.showAssignedDuty(incomingActivity) ? {} : employmentModel.assignedDuty
  );

  getPhysicalAddressUpdate = (incomingActivity: EmploymentActivityModel) => (
    this.showPhysicalAddress(incomingActivity) ? {} : employmentModel.physicalAddress
  );

  getSupervisorUpdate = (incomingActivity: EmploymentActivityModel) => {
    if (isPVQForm(this.props.formType)) {
      return this.showSupervisorPvq(incomingActivity) ? {} : employmentModel.supervisor;
    }

    return this.showSupervisor(incomingActivity) ? {} : employmentModel.supervisor;
  };

  getEmployedUpdate = (incomingActivity: EmploymentActivityModel) => (this.showEmployed(incomingActivity) ? {} : employmentModel.employed);

  updateEmploymentActivity = (values: EmploymentActivityModel) => {
    this.update({
      EmploymentActivity: values,
      ...this.getStatusUpdate(values),
      ...this.getReferenceUpdate(values),
      ...this.getEmployerUpdate(values),
      ...this.getSelfEmploymentUpdate(values),
      ...this.getUnemploymentUpdate(values),
      ...this.getAdditionalActivityUpdate(values),
      ...this.getAssignedDutyUpdate(values),
      ...this.getPhysicalAddressUpdate(values),
      ...this.getSupervisorUpdate(values),
      ...this.getEmployedUpdate(values),
      Reprimand: {},
    });
  };

  updateEmployment = (values: HistoryEmploymentItemModel['Employer']) => {
    this.update({
      Employer: values,
    });
  };

  onFederalAgencySuggestionSelected = (event, options, selectedAgency) => {
    if (selectedAgency) {
      this.update({
        Employer: { ...this.props.Employer, value: (selectedAgency || '') }
      });
    }
  };

  updateDates = (values: HistoryEmploymentItemModel['Dates']) => {
    this.update({
      Dates: values,
    });
  };

  updateTitle = (values: HistoryEmploymentItemModel['Title']) => {
    this.update({
      Title: values,
    });
  };

  updateMilitaryBranch = (values: HistoryEmploymentItemModel['MilitaryBranch']) => {
    this.update({
      MilitaryBranch: values,
    });
  };

  updateDutyStation = (values: HistoryEmploymentItemModel['DutyStation']) => {
    this.update({
      DutyStation: values,
    });
  };

  updateStatus = (values: HistoryEmploymentItemModel['Status']) => {
    this.update({
      Status: values,
    });
  };

  updateAddress = (values: HistoryEmploymentItemModel['Address']) => {
    this.update({
      Address: values,
    });
  };

  updateAssignments = (values: HistoryEmploymentItemModel['Assignments']) => {
    this.update({
      Assignments: values,
    });
  };

  updateTelephone = (values: HistoryEmploymentItemModel['Telephone']) => {
    this.update({
      Telephone: values,
    });
  };

  updateSupervisor = (values: HistoryEmploymentItemModel['Supervisor']) => {
    this.update({
      Supervisor: values,
    });
  };

  updateReferenceName = (values: HistoryEmploymentItemModel['ReferenceName']) => {
    this.update({
      ReferenceName: values,
    });
  };

  updateReferencePhone = (values: HistoryEmploymentItemModel['ReferencePhone']) => {
    this.update({
      ReferencePhone: values,
    });
  };

  updateReferenceAddress = (values: HistoryEmploymentItemModel['ReferenceAddress']) => {
    this.update({
      ReferenceAddress: values,
    });
  };

  updatePhysicalAddress = (values: HistoryEmploymentItemModel['PhysicalAddress']) => {
    this.update({
      PhysicalAddress: values,
    });
  };

  updatePhysicalAddressComments = (values: HistoryEmploymentItemModel['PhysicalAddressComments']) => {
    this.update({
      PhysicalAddressComments: values,
    });
  };

  updatePhysicalAddressAnswerComments = (values: HistoryEmploymentItemModel['PhysicalAddressAnswerComments']) => {
    this.update({
      PhysicalAddressAnswerComments: values,
    });
  };

  updateHasDifferentPhysicalWorkAddress = (values: HistoryEmploymentItemModel['HasDifferentPhysicalWorkAddress']) => {
    this.update({
      HasDifferentPhysicalWorkAddress: values,
    });
  };

  updatePhysicalWorkAddress = (values: HistoryEmploymentItemModel['PhysicalWorkAddress']) => {
    this.update({
      PhysicalWorkAddress: values,
    });
  };

  updateAdditional = (values: HistoryEmploymentItemModel['Additional']) => {
    this.update({
      Additional: values,
    });
  };

  updateReasonLeft = (values: HistoryEmploymentItemModel['ReasonLeft']) => {
    this.update({
      ReasonLeft: values,
    });
  };

  updateReprimand = (values: HistoryEmploymentItemModel['Reprimand']) => {
    this.update({
      Reprimand: values,
    });
  };

  updateEmploymentActivityComments = (values: HistoryEmploymentItemModel['EmploymentActivityComments']) => {
    this.update({
      EmploymentActivityComments: values,
    });
  };

  updateMilitaryEmploymentComments = (values: HistoryEmploymentItemModel['MilitaryEmploymentComments']) => {
    this.update({
      MilitaryEmploymentComments: values,
    });
  };

  updateNonMilitaryEmploymentComments = (values: HistoryEmploymentItemModel['NonMilitaryEmploymentComments']) => {
    this.update({
      NonMilitaryEmploymentComments: values,
    });
  };

  updateSupervisorAlternateAddressPhysicalComments = (values: HistoryEmploymentItemModel['SupervisorAlternateAddressPhysicalComments']) => {
    this.update({
      SupervisorAlternateAddressPhysicalComments: values,
    });
  };

  updateSupervisorAlternateAddressAnswerComments = (values: HistoryEmploymentItemModel['SupervisorAlternateAddressAnswerComments']) => {
    this.update({
      SupervisorAlternateAddressAnswerComments: values,
    });
  };

  updateSupervisorAlternateAddressComments = (values: HistoryEmploymentItemModel['SupervisorAlternateAddressComments']) => {
    this.update({
      SupervisorAlternateAddressComments: values,
    });
  };

  updateSupervisorComments = (values: SupervisorModel['Comments']) => {
    const supervisor = this.props.Supervisor || { Comments: {} };
    supervisor.Comments = values;
    this.update({
      Supervisor: supervisor,
    });
  };

  updateReasonLeftAnswerComments = (values: HistoryEmploymentItemModel['ReasonLeftAnswerComments']) => {
    this.update({
      ReasonLeftAnswerComments: values,
    });
  };

  updateReasonLeftComments = (values: HistoryEmploymentItemModel['ReasonLeftComments']) => {
    this.update({
      ReasonLeftComments: values,
    });
  };

  updateAddressPhysicalComments = (values: HistoryEmploymentItemModel['AddressPhysicalComments']) => {
    this.update({
      AddressPhysicalComments: values,
    });
  };

  updateAddressAnswerComments = (values: HistoryEmploymentItemModel['AddressAnswerComments']) => {
    this.update({
      AddressAnswerComments: values,
    });
  };

  updateAddressComments = (values: HistoryEmploymentItemModel['AddressComments']) => {
    this.update({
      AddressComments: values,
    });
  };

  updateReferenceComments = (values: HistoryEmploymentItemModel['ReferenceComments']) => {
    this.update({
      ReferenceComments: values,
    });
  };

  updateReferenceAlternateAddressPhysicalComments = (values: HistoryEmploymentItemModel['ReferenceAlternateAddressPhysicalComments']) => {
    this.update({
      ReferenceAlternateAddressPhysicalComments: values,
    });
  };

  updateReferenceAlternateAddressAnswerComments = (values: HistoryEmploymentItemModel['ReferenceAlternateAddressAnswerComments']) => {
    this.update({
      ReferenceAlternateAddressAnswerComments: values,
    });
  };

  updateReferenceAlternateAddressComments = (values: HistoryEmploymentItemModel['ReferenceAlternateAddressComments']) => {
    this.update({
      ReferenceAlternateAddressComments: values,
    });
  };

  updateSelfEmploymentComments = (values: HistoryEmploymentItemModel['SelfEmploymentComments']) => {
    this.update({
      SelfEmploymentComments: values,
    });
  };

  updateUnemploymentComments = (values: HistoryEmploymentItemModel['UnemploymentComments']) => {
    this.update({
      UnemploymentComments: values,
    });
  };

  updateReprimandAnswerComments = (values: HistoryEmploymentItemModel['ReprimandAnswerComments']) => {
    this.update({
      ReprimandAnswerComments: values,
    });
  };

  updateReprimandComments = (values: HistoryEmploymentItemModel['ReprimandComments']) => {
    this.update({
      ReprimandComments: values,
    });
  };

  updateStillInBusiness = (values: HistoryEmploymentItemModel['StillInBusiness']) => {
    const isOtherEmployment = this.showAdditionalActivity();
    const isStillInBusiness = values?.value === 'Yes';
    const clearInternalFields = !isStillInBusiness && isOtherEmployment;
    this.update({
      StillInBusiness: values,
      Telephone: clearInternalFields ? {} : this.props.Telephone,
      Address: clearInternalFields ? {} : this.props.Address,
    });
  };

  updateIsInternship = (values: HistoryEmploymentItemModel['IsInternship']) => {
    this.update({
      IsInternship: values,
    });
  };

  updateReturnedToWork = (values: HistoryEmploymentItemModel['ReturnedToWork']) => {
    this.update({
      ReturnedToWork: values,
    });
  };

  updateReturnDate = (values: HistoryEmploymentItemModel['ReturnDate']) => {
    this.update({
      ReturnDate: values,
    });
  };

  updateHasAssociatedCompany = (values: HistoryEmploymentItemModel['HasAssociatedCompany']) => {
    this.update({
      HasAssociatedCompany: values,
    });
  };

  updateSelfEmployedEmail = (values: HistoryEmploymentItemModel['SelfEmployedEmail']) => {
    this.update({
      SelfEmployedEmail: values,
    });
  };

  updateSelfEmployedEmailNotApplicable = (values: HistoryEmploymentItemModel['SelfEmployedEmailNotApplicable']) => {
    this.update({
      SelfEmployedEmail: (values?.applicable ? this.props.SelfEmployedEmail : {} as HistoryEmploymentItemModel['SelfEmployedEmail']),
      SelfEmployedEmailNotApplicable: values,
    });
  };

  updateSelfEmployedWebsite = (values: HistoryEmploymentItemModel['SelfEmployedWebsite']) => {
    this.update({
      SelfEmployedWebsite: values,
    });
  };

  updateSelfEmployedWebsiteNotApplicable = (values: HistoryEmploymentItemModel['SelfEmployedWebsiteNotApplicable']) => {
    this.update({
      SelfEmployedWebsite: (values?.applicable ? this.props.SelfEmployedWebsite : {} as HistoryEmploymentItemModel['SelfEmployedWebsite']),
      SelfEmployedWebsiteNotApplicable: values,
    });
  };

  updateIsHomeCompany = (values: HistoryEmploymentItemModel['IsHomeCompany']) => {
    this.update({
      IsHomeCompany: values,
    });
  };

  updateDismissalBranch = (values: YesOrNoModel) => {
    const updateFields: Partial<HistoryEmploymentItemModel> = {};
    switch (values.name) {
      case `was_${employmentDimissalTypes.leaveAfterNoticeMisconduct}`:
        updateFields.WasLeaveAfterNoticeMisconduct = { value: values.value };
        break;
      case `was_${employmentDimissalTypes.leaveAfterNoticePerformance}`:
        updateFields.WasLeaveAfterNoticePerformance = { value: values.value };
        break;
      case `was_${employmentDimissalTypes.fired}`:
        updateFields.WasFired = { value: values.value };
        break;
      case `was_${employmentDimissalTypes.leaveBeforeFired}`:
        updateFields.WasLeaveBeforeFired = { value: values.value };
        break;
      case `was_${employmentDimissalTypes.leavePendingOutcome}`:
        updateFields.WasLeavePendingOutcome = { value: values.value };
        break;
      default:
        break;
    }
    if (values.value === 'No') {
      switch (values.name) {
        case `was_${employmentDimissalTypes.leaveAfterNoticeMisconduct}`:
          updateFields.LeaveAfterNoticeMisconductDetails = {};
          break;
        case `was_${employmentDimissalTypes.leaveAfterNoticePerformance}`:
          updateFields.LeaveAfterNoticePerformanceDetails = {};
          break;
        case `was_${employmentDimissalTypes.fired}`:
          updateFields.FiredDetails = {};
          break;
        case `was_${employmentDimissalTypes.leaveBeforeFired}`:
          updateFields.LeaveBeforeFiredDetails = {};
          break;
        case `was_${employmentDimissalTypes.leavePendingOutcome}`:
          updateFields.LeavePendingOutcomeDetails = {};
          break;
        default:
          break;
      }
    }
    this.update({
      ...updateFields
    });
  };

  updateDismissalItem = (values: Partial<DismissalModel>, dismissalType: string) => {
    if (!dismissalType || !values) {
      return;
    }
    const updateFields: Partial<HistoryEmploymentItemModel> = {};
    switch (dismissalType) {
      case employmentDimissalTypes.leaveAfterNoticeMisconduct:
        updateFields.LeaveAfterNoticeMisconductDetails = values;
        break;
      case employmentDimissalTypes.leaveAfterNoticePerformance:
        updateFields.LeaveAfterNoticePerformanceDetails = values;
        break;
      case employmentDimissalTypes.fired:
        updateFields.FiredDetails = values;
        break;
      case employmentDimissalTypes.leaveBeforeFired:
        updateFields.LeaveBeforeFiredDetails = values;
        break;
      case employmentDimissalTypes.leavePendingOutcome:
        updateFields.LeavePendingOutcomeDetails = values;
        break;
      default:
        break;
    }
    this.update({
      ...updateFields
    });
  };

  getActivity = (incomingActivity?: EmploymentActivityModel) => (
    incomingActivity !== undefined ? incomingActivity?.value : (this.props.EmploymentActivity || {}).value
  );

  showStatus = (incomingActivity?: EmploymentActivityModel) => {
    const activity = this.getActivity(incomingActivity);

    if (isPVQForm(this.props.formType)) {
      return activity && !['Unemployment'].includes(activity);
    }

    return (
      activity && this.props.requireEmploymentItemStatus && !['Unemployment'].includes(activity)
    );
  };

  showSelfEmployment = (incomingActivity?: EmploymentActivityModel) => {
    const activity = this.getActivity(incomingActivity);
    return (
      activity && ['SelfEmployment'].includes(activity)
    );
  };

  showUnemployment = (incomingActivity?: EmploymentActivityModel) => {
    const activity = this.getActivity(incomingActivity);
    return (
      activity && ['Unemployment'].includes(activity)
    );
  };

  showDismissalDetails = (incomingActivity?: EmploymentActivityModel) => {
    const activity = this.getActivity(incomingActivity);
    return (
      activity
      && ['OtherFederal', 'FederalContractor', 'StateGovernment', 'NonGovernment'].includes(
        activity
      )
    );
  };

  showAdditionalActivity = (incomingActivity?: EmploymentActivityModel) => {
    const activity = this.getActivity(incomingActivity);
    return (
      activity
      && ['OtherFederal', 'StateGovernment', 'FederalContractor', 'Other', 'NonGovernment'].includes(
        activity
      )
    );
  };

  showReference = (incomingActivity?: EmploymentActivityModel) => {
    const activity = this.getActivity(incomingActivity);
    return activity && ['SelfEmployment', 'Unemployment'].includes(activity);
  };

  showAssignedDuty = (incomingActivity?: EmploymentActivityModel) => {
    const activity = this.getActivity(incomingActivity);
    return activity && ['ActiveMilitary', 'NationalGuard', 'USPHS'].includes(activity);
  };

  showAssignedDutyPvq = (incomingActivity?: EmploymentActivityModel) => {
    const activity = this.getActivity(incomingActivity);
    return activity && ['USMilitary'].includes(activity);
  };

  showMilitaryPvq = (incomingActivity?: EmploymentActivityModel) => {
    const activity = this.getActivity(incomingActivity);
    return activity && ['USMilitary'].includes(activity);
  };

  showEmployer = (incomingActivity?: EmploymentActivityModel) => {
    const activity = this.getActivity(incomingActivity);
    return (
      activity && !['Unemployment', 'ActiveMilitary', 'NationalGuard', 'USPHS'].includes(activity)
    );
  };

  showEmployerPvq = (incomingActivity?: EmploymentActivityModel) => {
    const activity = this.getActivity(incomingActivity);
    return (
      activity && !['Unemployment', 'USMilitary'].includes(activity)
    );
  };

  showPhysicalAddress = (incomingActivity?: EmploymentActivityModel) => {
    const activity = this.getActivity(incomingActivity);
    return (
      activity
      && (['OtherFederal', 'StateGovernment', 'FederalContractor', 'NonGovernment', 'Other'].includes(
        activity
      )
        || (['SelfEmployment'].includes(activity)
          && this.props.requireEmploymentSelfEmployedJobLocation))
    );
  };

  showSupervisor = (incomingActivity?: EmploymentActivityModel) => {
    const activity = this.getActivity(incomingActivity);
    return (
      activity
      && [
        'ActiveMilitary',
        'NationalGuard',
        'USPHS',
        'OtherFederal',
        'StateGovernment',
        'FederalContractor',
        'NonGovernment',
        'Other',
      ].includes(activity)
    );
  };

  showSupervisorPvq = (incomingActivity?: EmploymentActivityModel) => {
    const activity = this.getActivity(incomingActivity);
    return (
      activity
      && [
        'USMilitary',
        'OtherFederal',
        'StateGovernment',
        'FederalContractor',
        'NonGovernment',
        'SelfEmployment',
        'Unemployment',
        'Other',
      ].includes(activity)
    );
  };

  showEmployed = (incomingActivity?: EmploymentActivityModel) => {
    const activity = this.getActivity(incomingActivity);
    return activity && !['Unemployment'].includes(activity);
  };

  /**
   * Only show the reasons for leaving if
   *
   *  - employed there within the last # years
   *  - employed by:
   *    - Active Military
   *    - National Guard
   *    - USPHS
   *    - Other Federal
   *    - State Government
   *    - Federal Contractor
   *    - Non-Government
   *    - Self Employment
   *    - Other
   */
  showLeaving = (years = 7) => {
    const activity = this.getActivity();
    const now = new Date();
    const recordYearsAgo = new Date(now.getFullYear() - years, now.getMonth(), 1);
    const dates = this.props.Dates || {};
    const from = extractDate(dates.from);
    const to = dates.present === true ? now : extractDate(dates.to);

    // Check user is within seven years and part of approved employers.
    return (
      (from && from >= recordYearsAgo)
      || (to
        && to >= recordYearsAgo
        && [
          'ActiveMilitary',
          'NationalGuard',
          'USPHS',
          'OtherFederal',
          'StateGovernment',
          'FederalContractor',
          'NonGovernment',
          'SelfEmployment',
          'Other',
        ].includes(activity))
    );
  };

  localizeByActivity = () => {
    const activity = this.getActivity() || 'default';
    return activity.toLowerCase();
  };

  // #endregion Controller

  render() {
    const { recordYears, formType } = this.props;
    const activity = this.getActivity();
    const prefix = `history.employment.${this.localizeByActivity()}`.trim();
    const pvqPrefix = 'history.pvq.employment';
    const pvqPrefixWithActivity = `history.pvq.employment.${this.localizeByActivity()}`.trim();
    const isPvqMilitary = (isPVQForm(formType) && this.props.EmploymentActivity?.value === 'USMilitary');
    let addressTitle = i18n.t(`${pvqPrefix}.heading.address`);
    let addressLabel = i18n.t(`${prefix}.address.label`);

    const hasDifferentPhysicalAddress = this.props.PhysicalAddress?.HasDifferentAddress?.value === 'Yes';

    const showAdditionalActivity = this.showAdditionalActivity();
    const isMilitaryEmployment = this.showMilitaryPvq();
    const isOtherEmployment = this.showAdditionalActivity();
    const isSelfEmployment = this.showSelfEmployment();
    const isUnemployment = this.showUnemployment();

    const attribute = `List.items[${this.props.$index}].Item`;

    const isPvq = isPVQForm(formType);

    let employmentDateText = i18n.t('history.employment.default.heading.datesEmployed');
    const addressDomesticTitle = isPvqMilitary ? i18n.t(`${pvqPrefixWithActivity}.address.domesticTitle`) : i18n.t(`history.pvq.employment.default.domesticTitle`);
    const addressForeignTitle = isPvqMilitary ? i18n.t(`${pvqPrefixWithActivity}.address.foreignTitle`) : i18n.t(`history.pvq.employment.default.foreignTitle`);
    if (isPvq) {
      if (isPvqMilitary) {
        employmentDateText = i18n.t(`${pvqPrefix}.usmilitary.heading.datesEmployed`);
        addressTitle = i18n.t(`${pvqPrefixWithActivity}.heading.address`);
        addressLabel = i18n.t(`${pvqPrefixWithActivity}.address.label`);
      } else {
        employmentDateText = i18n.t('history.pvq.employment.default.heading.datesEmployed');
        addressTitle = i18n.t(`${pvqPrefix}.heading.address`);
        addressLabel = i18n.t(`history.pvq.employment.default.isInUS`);
      }
    }

    return isPvq ? (
      // #region PVQView
      <div>
        <Field
          title={i18n.t('history.pvq.employment.heading.activity')}
          scrollIntoView={this.props.scrollIntoView}
          attribute="EmploymentActivity"
        >
          <RadioGroup
            className="employment-activity option-list option-list-vertical"
            selectedValue={this.props.EmploymentActivity?.value}
            required={this.props.required}
            onError={this.props.onError}
            attribute={`${attribute}.EmploymentActivity`}
          >
            {employmentActivityTypes.map((x, index) => (
              <Radio
                name="EmploymentActivity"
                key={`${x.key}-${index}`}
                value={`${x.value}`}
                label={i18n.t(`history.pvq.employment.default.collection.activityTypes.${x.i18nField}`)}
                onUpdate={this.updateEmploymentActivity}
                onError={this.props.onError}
              />
            ))}
          </RadioGroup>
        </Field>
        {isMilitaryEmployment && (
          <>
            <Field
              title={i18n.t(`${pvqPrefix}.usmilitary.heading.militaryBranch`)}
              scrollIntoView={this.props.scrollIntoView}
              attribute="MilitaryBranch"
            >
              <RadioGroup
                className="military-branch option-list option-list-vertical"
                selectedValue={this.props.MilitaryBranch?.value}
                required={this.props.required}
                onError={this.props.onError}
                attribute={`${attribute}.MilitaryBranch`}
              >
                {employmentMilitaryBranchValues.map((x, index) => (
                  <Radio
                    name="EmploymentMilitaryBranch"
                    key={`${x.key}-${index}`}
                    value={`${x.value}`}
                    label={i18n.t(`history.pvq.employment.default.collection.employmentMilitaryBranches.${x.i18nField}`)}
                    onUpdate={this.updateMilitaryBranch}
                    onError={this.props.onError}
                  />
                ))}
              </RadioGroup>
            </Field>

            <Field
              attribute={`${attribute}.DutyStation`}
              title={i18n.t(`${pvqPrefix}.usmilitary.heading.dutyStation`)}
              titleSize="h4"
              adjustFor="labels"
              scrollIntoView={this.props.scrollIntoView}
            >
              <Text
                attribute={`${attribute}.DutyStation`}
                name="DutyStation"
                {...this.props.DutyStation}
                onUpdate={this.updateDutyStation}
                className="text full-width employment-duty-station"
                onError={this.props.onError}
                required={this.props.required}
              />
            </Field>
            <Field
              attribute={`${attribute}.Dates`}
              title={employmentDateText}
              titleSize="h4"
              help="history.employment.default.datesEmployed.help"
              adjustFor="daterange"
              shrink
              scrollIntoView={this.props.scrollIntoView}
            >
              <DateRange
                attribute={`${attribute}.Dates`}
                name="Dates"
                {...this.props.Dates}
                minDateEqualTo
                onUpdate={this.updateDates}
                onError={this.props.onError}
                required={this.props.required}
                hideFromDay
                hideToDay
              />
            </Field>
            <Show when={this.showEmployed()}>
              <Field
                attribute={`${attribute}.Address`}
                optional
                onError={this.props.onError}
                adjustFor="address"
                shrink
                scrollIntoView={this.props.scrollIntoView}
              >
                <PVQLocation
                  attribute={`${attribute}.Address`}
                  name="Address"
                  {...this.props.Address}
                  label={addressLabel}
                  domesticTitle={addressDomesticTitle}
                  foreignTitle={addressForeignTitle}
                  includeMilitaryStates
                  layout={Location.ADDRESS}
                  geocode
                  addressBook="Employment"
                  addressBooks={this.props.addressBooks}
                  dispatch={this.props.dispatch}
                  onUpdate={this.updateAddress}
                  onError={this.props.onError}
                  required={this.props.required}
                  isPoBoxAllowed={false}
                  showInternationalStreet={false}
                />
              </Field>
            </Show>
            <Assignments
              onUpdate={this.updateAssignments}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.Assignments`}
              appendFieldTitle={i18n.t('history.pvq.employment.default.assignment.append')}
              branchFieldTitle={i18n.t('history.pvq.employment.default.assignment.branch.title')}
              {...this.props.Assignments}
              hideInitialBranchQuestionAssumeYes
            />
            <Supervisor
              attribute={`${attribute}.Supervisor`}
              nonMilitary={showAdditionalActivity}
              name="Supervisor"
              {...this.props.Supervisor}
              addressBooks={this.props.addressBooks}
              dispatch={this.props.dispatch}
              onUpdate={this.updateSupervisor}
              onError={this.props.onError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              formType={this.props.formType}
              updateSupervisorComments={this.updateSupervisorComments}
              Comments={this.props.Supervisor.Comments}
              requireEmploymentHistorySupervisorEmail={
                this.props.requireEmploymentHistorySupervisorEmail
              }
              requireSupervisorHasDifferentAddress={this.props.requireSupervisorHasDifferentAddress}
              requireSupervisorAddress={this.props.requireSupervisorAddress}
            />
            <Show
              when={
                this.props.requireEmploymentItemLeft
              }
            >
              <PvqReprimand
                attribute={`${attribute}.Reprimand`}
                name="Reprimand"
                className="reprimand"
                {...this.props.Reprimand}
                Dates={this.props.Dates}
                onUpdate={this.updateReprimand}
                onError={this.props.onError}
                required={this.props.required}
                scrollIntoView={this.props.scrollIntoView}
                activity={this.props.EmploymentActivity?.value}
                branchLabel={this.showAssignedDutyPvq()
                  ? i18n.t('history.pvq.employment.default.reprimand.militaryLabel')
                  : i18n.t('history.pvq.employment.default.reprimand.label')}
                appendLabel={this.showAssignedDutyPvq()
                  ? i18n.t('history.pvq.employment.default.reprimand.militaryAppend')
                  : i18n.t('history.pvq.employment.default.reprimand.append')}
                hideGivenByEmail={this.showAssignedDutyPvq()}
                hideGivenByTelephone={this.showAssignedDutyPvq()}
                showConsequences={this.showAssignedDutyPvq()}
                showGivenByRank={this.showAssignedDutyPvq()}
                useMilitaryDisciplineActions={this.showAssignedDutyPvq()}
                hideDateDay
              />
            </Show>

          </>

        )}
        {isOtherEmployment && (
          <>
            <Field
              attribute={`${attribute}.Employer`}
              title={i18n.t(`${pvqPrefix}.heading.employer`)}
              titleSize="h4"
              scrollIntoView={this.props.scrollIntoView}
            > {
                (activity === 'OtherFederal')
                  ? (
                    <FederalAgencyAutoComplete
                      name="Employer"
                      className="federal-agency-auto"
                      attribute={`${attribute}.Employer`}
                      onUpdate={this.updateEmployment}
                      onError={this.props.onError}
                      required={this.props.required}
                      value={this.props.Employer?.value}
                      warningMessage={i18n.t('history.federal.agencyDropdownWarning')}
                      onSuggestionSelected={this.onFederalAgencySuggestionSelected}
                    />
                  )
                  : (
                    <Text
                      attribute={`${attribute}.Employer`}
                      name="Employer"
                      {...this.props.Employer}
                      onUpdate={this.updateEmployment}
                      onError={this.props.onError}
                      className="text full-width employment"
                      required={this.props.required}
                    />
                  )
              }
            </Field>
            <Field
              attribute={`${attribute}.Dates`}
              title={employmentDateText}
              titleSize="h4"
              help="history.employment.default.datesEmployed.help"
              adjustFor="daterange"
              shrink
              scrollIntoView={this.props.scrollIntoView}
            >
              <DateRange
                attribute={`${attribute}.Dates`}
                name="Dates"
                {...this.props.Dates}
                minDateEqualTo
                onUpdate={this.updateDates}
                onError={this.props.onError}
                required={this.props.required}
                hideFromDay
                hideToDay
              />
            </Field>
            <Field
              attribute={`${attribute}.StillInBusiness`}
              className="stillI-in-business"
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            >
              <Branch
                {...this.props.StillInBusiness}
                attribute={`${attribute}.StillInBusiness`}
                name="StillInBusiness"
                label={i18n.t(`${pvqPrefix}.heading.stillInBusiness`)}
                labelSize="h4"
                className="still-in-business"
                onUpdate={this.updateStillInBusiness}
                required={this.props.required}
                scrollIntoView={this.props.scrollIntoView}
                onError={this.props.onError}
                showDNK
              />
            </Field>
            <Show
              when={this.props?.StillInBusiness?.value === 'Yes'}
            >
              <Field
                attribute={`${attribute}.Telephone`}
                title={i18n.t(`${pvqPrefix}.default.heading.telephone`)}
                titleSize="h4"
                className="override-required"
                adjustFor="telephone"
                scrollIntoView={this.props.scrollIntoView}
              >
                <Telephone
                  attribute={`${attribute}.Telephone`}
                  name="Telephone"
                  {...this.props.Telephone}
                  onUpdate={this.updateTelephone}
                  allowNotApplicable={false}
                  onError={this.props.onError}
                  required={this.props.required}
                  showNumberType
                  showTimeOfDay
                  timeOfDayLabel={i18n.t('identification.pvq.contacts.collection.phoneNumbers.timeOfDayLabel')}
                />
              </Field>
              <Field
                attribute={`${attribute}.Address`}
                optional
                onError={this.props.onError}
                adjustFor="address"
                shrink
                scrollIntoView={this.props.scrollIntoView}
              >
                <LocationUnknown
                  attribute={`${attribute}.Address`}
                  name="Address"
                  {...this.props.Address}
                  label={addressLabel}
                  domesticTitle={addressDomesticTitle}
                  foreignTitle={addressForeignTitle}
                  internationalStreetLabel={i18n.t('address.pvq.international.street.label')}
                  includeMilitaryStates
                  layout={LocationUnknown.ADDRESS}
                  geocode
                  addressBook="Employment"
                  addressBooks={this.props.addressBooks}
                  dispatch={this.props.dispatch}
                  onUpdate={this.updateAddress}
                  onError={this.props.onError}
                  required={this.props.required}
                  isPoBoxAllowed={false}
                  showMilitaryInstallation
                  showInternationalStreet
                  showApoFpoDpoPostcodeWhenForeign
                  hideInstallationNameUnknownExplanation
                  hideInstallationNameUnknownWhenForeign
                />
              </Field>
            </Show>
            <Field
              attribute={`${attribute}.HasDifferentPhysicalWorkAddress`}
              className="explanation-left"
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            >
              <Branch
                {...this.props.HasDifferentPhysicalWorkAddress}
                attribute={`${attribute}.HasDifferentPhysicalWorkAddress`}
                name="HasDifferentPhysicalWorkAddress"
                label={i18n.t(`${pvqPrefix}.heading.hasDifferentPhysical`)}
                labelSize="h4"
                className="employment-departure-branch"

                onUpdate={this.updateHasDifferentPhysicalWorkAddress}
                required={this.props.required}
                scrollIntoView={this.props.scrollIntoView}
                onError={this.props.onError}
              />
            </Field>
            <Show when={this.props?.HasDifferentPhysicalWorkAddress?.value === 'Yes'}>
              <Field
                attribute={`${attribute}.PhysicalWorkAddress`}
                optional
                onError={this.props.onError}
                adjustFor="address"
                shrink
                scrollIntoView={this.props.scrollIntoView}
              >
                <LocationUnknown
                  attribute={`${attribute}.PhysicalWorkAddress`}
                  name="PhysicalWorkAddress"
                  {...this.props.PhysicalWorkAddress}
                  label={i18n.t(`${pvqPrefix}.heading.physicalAddress`)}
                  domesticTitle={i18n.t('history.pvq.employment.default.address.domesticTitle')}
                  foreignTitle={i18n.t('history.pvq.employment.default.address.foreignTitle')}
                  internationalStreetLabel={i18n.t('address.pvq.international.street.label')}
                  includeMilitaryStates
                  layout={LocationUnknown.ADDRESS}
                  geocode
                  addressBook="Employment"
                  addressBooks={this.props.addressBooks}
                  dispatch={this.props.dispatch}
                  onUpdate={this.updatePhysicalWorkAddress}
                  onError={this.props.onError}
                  required={this.props.required}
                  isPoBoxAllowed={false}
                  showMilitaryInstallation
                  showInternationalStreet
                  showApoFpoDpoPostcodeWhenForeign
                  hideInstallationNameUnknownExplanation
                  hideInstallationNameUnknownWhenForeign
                />
              </Field>
            </Show>
            <Field
              attribute={`${attribute}.Title`}
              title={i18n.t(`${pvqPrefix}.heading.title`)}
              titleSize="h4"
              adjustFor="labels"
              scrollIntoView={this.props.scrollIntoView}
            >
              <Text
                attribute={`${attribute}.Title`}
                name="Title"
                {...this.props.Title}
                onUpdate={this.updateTitle}
                className="text employment-title"
                onError={this.props.onError}
                required={this.props.required}
              />
            </Field>
            <Field
              attribute={`${attribute}.IsInternship`}
              className="stillI-in-business"
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            >
              <Branch
                {...this.props.IsInternship}
                attribute={`${attribute}.IsInternship`}
                name="IsInternship"
                label={i18n.t(`${pvqPrefix}.heading.isInternship`)}
                labelSize="h4"
                className="is-internship"
                onUpdate={this.updateIsInternship}
                required={this.props.required}
                scrollIntoView={this.props.scrollIntoView}
                onError={this.props.onError}
              />
            </Field>
            <Field
              attribute={`${attribute}.Status`}
              title={i18n.t(`${pvqPrefix}.nongovernment.heading.status`)}
              titleSize="h4"
              shrink
              scrollIntoView={this.props.scrollIntoView}
            >
              <EmploymentStatus
                attribute={`${attribute}.Status`}
                name="Status"
                {...this.props.Status}
                onUpdate={this.updateStatus}
                onError={this.props.onError}
                required={this.props.required}
              />
            </Field>
            <Supervisor
              attribute={`${attribute}.Supervisor`}
              nonMilitary={showAdditionalActivity}
              name="Supervisor"
              {...this.props.Supervisor}
              addressBooks={this.props.addressBooks}
              dispatch={this.props.dispatch}
              onUpdate={this.updateSupervisor}
              onError={this.props.onError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              formType={this.props.formType}
              updateSupervisorComments={this.updateSupervisorComments}
              Comments={this.props.Supervisor.Comments}
              requireEmploymentHistorySupervisorEmail={
                this.props.requireEmploymentHistorySupervisorEmail
              }
              requireSupervisorHasDifferentAddress={this.props.requireSupervisorHasDifferentAddress}
              requireSupervisorAddress={this.props.requireSupervisorAddress}
              showMultipleEmails
              showMultipleTelephones
              includeEmailType={false}
            />
            <Show
              when={this.props.requireEmploymentItemLeft}
            >
              <PvqReprimand
                attribute={`${attribute}.Reprimand`}
                name="Reprimand"
                className="reprimand"
                {...this.props.Reprimand}
                Dates={this.props.Dates}
                onUpdate={this.updateReprimand}
                onError={this.props.onError}
                required={this.props.required}
                scrollIntoView={this.props.scrollIntoView}
                activity={this.props.EmploymentActivity?.value}
                branchLabel={this.showAssignedDutyPvq()
                  ? i18n.t('history.pvq.employment.default.reprimand.militaryLabel')
                  : i18n.t('history.pvq.employment.default.reprimand.label')}
                appendLabel={this.showAssignedDutyPvq()
                  ? i18n.t('history.pvq.employment.default.reprimand.militaryAppend')
                  : i18n.t('history.pvq.employment.default.reprimand.append')}
                hideGivenByEmail={this.showAssignedDutyPvq()}
                hideGivenByTelephone={this.showAssignedDutyPvq()}
                showConsequences={this.showAssignedDutyPvq()}
                showGivenByRank={this.showAssignedDutyPvq()}
                useMilitaryDisciplineActions={this.showAssignedDutyPvq()}
                hideDateDay
              />
            </Show>
            {this.props.requireEmploymentItemLeft && (
              <>
                <Branch
                  attribute={`${attribute}.WasFired`}
                  name={`was_${employmentDimissalTypes.fired}`}
                  label={i18n.t(`${pvqPrefix}.default.departure.${employmentDimissalTypes.fired}.label`)}
                  labelSize="h4"
                  className="employment-departure-branch"
                  {...this.props.WasFired}
                  onUpdate={this.updateDismissalBranch}
                  required={this.props.required}
                  scrollIntoView={this.props.scrollIntoView}
                  onError={this.props.onError}
                />
                {this.props.WasFired?.value === 'Yes' && (

                  <DismissalItem
                    attribute={`${attribute}.FiredDetails`}
                    dismissalType={employmentDimissalTypes.fired}
                    onUpdate={this.updateDismissalItem}
                    onError={this.props.onError}
                    required
                    bind
                    scrollIntoView={this.props.scrollIntoView}
                    {...this.props.FiredDetails}
                  />
                )}

                <Branch
                  attribute={`${attribute}.WasLeaveBeforeFired`}
                  name={`was_${employmentDimissalTypes.leaveBeforeFired}`}
                  label={i18n.t(`${pvqPrefix}.default.departure.${employmentDimissalTypes.leaveBeforeFired}.label`)}
                  labelSize="h4"
                  className="employment-departure-branch"
                  {...this.props.WasLeaveBeforeFired}
                  onUpdate={this.updateDismissalBranch}
                  required={this.props.required}
                  scrollIntoView={this.props.scrollIntoView}
                  onError={this.props.onError}
                />
                {this.props.WasLeaveBeforeFired?.value === 'Yes' && (

                  <DismissalItem
                    attribute={`${attribute}.LeaveBeforeFiredDetails`}
                    dismissalType={employmentDimissalTypes.leaveBeforeFired}
                    onUpdate={this.updateDismissalItem}
                    onError={this.props.onError}
                    required
                    bind
                    scrollIntoView={this.props.scrollIntoView}
                    {...this.props.LeaveBeforeFiredDetails}
                  />
                )}

                <Branch
                  attribute={`${attribute}.WasLeaveAfterNoticeMisconduct`}
                  name={`was_${employmentDimissalTypes.leaveAfterNoticeMisconduct}`}
                  label={i18n.t(`${pvqPrefix}.default.departure.${employmentDimissalTypes.leaveAfterNoticeMisconduct}.label`)}
                  labelSize="h4"
                  className="employment-departure-branch"
                  {...this.props.WasLeaveAfterNoticeMisconduct}
                  onUpdate={this.updateDismissalBranch}
                  required={this.props.required}
                  scrollIntoView={this.props.scrollIntoView}
                  onError={this.props.onError}
                />
                {this.props.WasLeaveAfterNoticeMisconduct?.value === 'Yes' && (

                  <DismissalItem
                    attribute={`${attribute}.LeaveAfterNoticeMisconductDetails`}
                    dismissalType={employmentDimissalTypes.leaveAfterNoticeMisconduct}
                    onUpdate={this.updateDismissalItem}
                    onError={this.props.onError}
                    required
                    bind
                    scrollIntoView={this.props.scrollIntoView}
                    {...this.props.LeaveAfterNoticeMisconductDetails}
                  />
                )}
                <Branch
                  attribute={`${attribute}.WasLeaveAfterNoticePerformance`}
                  name={`was_${employmentDimissalTypes.leaveAfterNoticePerformance}`}
                  label={i18n.t(`${pvqPrefix}.default.departure.${employmentDimissalTypes.leaveAfterNoticePerformance}.label`)}
                  labelSize="h4"
                  className="employment-departure-branch"
                  {...this.props.WasLeaveAfterNoticePerformance}
                  onUpdate={this.updateDismissalBranch}
                  required={this.props.required}
                  scrollIntoView={this.props.scrollIntoView}
                  onError={this.props.onError}
                />
                {this.props.WasLeaveAfterNoticePerformance?.value === 'Yes' && (

                  <DismissalItem
                    attribute={`${attribute}.LeaveAfterNoticePerformanceDetails`}
                    dismissalType={employmentDimissalTypes.leaveAfterNoticePerformance}
                    onUpdate={this.updateDismissalItem}
                    onError={this.props.onError}
                    required
                    bind
                    scrollIntoView={this.props.scrollIntoView}
                    {...this.props.LeaveAfterNoticePerformanceDetails}
                  />
                )}

                <Branch
                  attribute={`${attribute}.WasLeavePendingOutcome`}
                  name={`was_${employmentDimissalTypes.leavePendingOutcome}`}
                  label={i18n.t(`${pvqPrefix}.default.departure.${employmentDimissalTypes.leavePendingOutcome}.label`)}
                  labelSize="h4"
                  className="employment-departure-branch"
                  {...this.props.WasLeavePendingOutcome}
                  onUpdate={this.updateDismissalBranch}
                  required={this.props.required}
                  scrollIntoView={this.props.scrollIntoView}
                  onError={this.props.onError}
                />
                {this.props.WasLeavePendingOutcome?.value === 'Yes' && (

                  <DismissalItem
                    attribute={`${attribute}.LeavePendingOutcomeDetails`}
                    dismissalType={employmentDimissalTypes.leavePendingOutcome}
                    onUpdate={this.updateDismissalItem}
                    onError={this.props.onError}
                    required
                    bind
                    scrollIntoView={this.props.scrollIntoView}
                    {...this.props.LeavePendingOutcomeDetails}
                  />
                )}

                <ReasonLeft
                  attribute={`${attribute}.ReasonLeft`}
                  name="ReasonLeft"
                  recordYears={recordYears}
                  Dates={this.props.Dates}
                  {...this.props.ReasonLeft}
                  onUpdate={this.updateReasonLeft}
                  onError={this.props.onError}
                  required={this.props.required}
                  scrollIntoView={this.props.scrollIntoView}
                  formType={this.props.formType}
                />

                <Field
                  attribute={`${attribute}.ReturnedToWork`}
                  className="ReturnedToWork"
                  required={this.props.required}
                  scrollIntoView={this.props.scrollIntoView}
                >
                  <Branch
                    {...this.props.ReturnedToWork}
                    attribute={`${attribute}.ReturnedToWork`}
                    name="ReturnedToWork"
                    label={i18n.t(`history.pvq.employment.otherfederal.returnToWork`)}
                    labelSize="h4"
                    className="ReturnedToWork"
                    onUpdate={this.updateReturnedToWork}
                    required={this.props.required}
                    scrollIntoView={this.props.scrollIntoView}
                    onError={this.props.onError}
                  />
                </Field>
                {this.props?.ReturnedToWork?.value === 'Yes' && (
                  <Field
                    attribute={`${attribute}.ReturnDate`}
                    title={employmentDateText}
                    titleSize="h4"
                    className="date-left"
                    adjustFor="labels"
                    shrink
                    scrollIntoView={this.props.scrollIntoView}
                  >
                    <DateRange
                      attribute={`${attribute}.ReturnDate`}
                      name="ReturnDate"
                      {...this.props.ReturnDate}
                      onUpdate={this.updateReturnDate}
                      minDateEqualTo
                      onError={this.props.onError}
                      required={this.props.required}
                      hideFromDay
                      hideToDay
                    />

                  </Field>
                )}
              </>
            )}
          </>
        )}
        {isSelfEmployment && (
          <>
            <Field
              attribute={`${attribute}.HasAssociatedCompany`}
              className="HasAssociatedCompany"
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            >
              <Branch
                {...this.props.HasAssociatedCompany}
                attribute={`${attribute}.HasAssociatedCompany`}
                name="HasAssociatedCompany"
                label={i18n.t(`history.pvq.employment.selfemployment.heading.hasAssociatedCompany`)}
                labelSize="h4"
                className="HasAssociatedCompany"
                onUpdate={this.updateHasAssociatedCompany}
                required={this.props.required}
                scrollIntoView={this.props.scrollIntoView}
                onError={this.props.onError}
              />
            </Field>
            {this.props?.HasAssociatedCompany?.value === 'Yes' && (
              <>
                <Field
                  attribute={`${attribute}.Employer`}
                  title={i18n.t(`history.pvq.employment.selfemployment.heading.employer`)}
                  titleSize="h4"
                  adjustFor="labels"
                  scrollIntoView={this.props.scrollIntoView}
                >
                  <Text
                    attribute={`${attribute}.Employer`}
                    name="Employer"
                    {...this.props.Employer}
                    onUpdate={this.updateEmployment}
                    onError={this.props.onError}
                    className="text full-width employment"
                    required={this.props.required}
                  />
                </Field>
                <Field
                  attribute={`${attribute}.StillInBusiness`}
                  className="stillI-in-business"
                  required={this.props.required}
                  scrollIntoView={this.props.scrollIntoView}
                >
                  <Branch
                    {...this.props.StillInBusiness}
                    attribute={`${attribute}.StillInBusiness`}
                    name="StillInBusiness"
                    label={i18n.t(`history.pvq.employment.selfemployment.heading.stillInBusiness`)}
                    labelSize="h4"
                    className="still-in-business"
                    onUpdate={this.updateStillInBusiness}
                    required={this.props.required}
                    scrollIntoView={this.props.scrollIntoView}
                    onError={this.props.onError}
                  />
                </Field>
              </>
            )}
            <Field
              attribute={`${attribute}.Dates`}
              title={i18n.t('history.pvq.employment.selfemployment.heading.dates')}
              titleSize="h4"
              help="history.employment.default.datesEmployed.help"
              adjustFor="daterange"
              shrink
              scrollIntoView={this.props.scrollIntoView}
            >
              <DateRange
                attribute={`${attribute}.Dates`}
                name="Dates"
                {...this.props.Dates}
                minDateEqualTo
                onUpdate={this.updateDates}
                onError={this.props.onError}
                required={this.props.required}
                hideFromDay
                hideToDay
              />
            </Field>
            <Field
              attribute={`${attribute}.Title`}
              title={i18n.t(`${pvqPrefix}.heading.title`)}
              titleSize="h4"
              adjustFor="labels"
              scrollIntoView={this.props.scrollIntoView}
            >
              <Text
                attribute={`${attribute}.Title`}
                name="Title"
                {...this.props.Title}
                onUpdate={this.updateTitle}
                className="text employment-title"
                onError={this.props.onError}
                required={this.props.required}
              />
            </Field>
            <Field
              attribute={`${attribute}.Status`}
              title={i18n.t(`history.pvq.employment.selfemployment.heading.status`)}
              titleSize="h4"
              shrink
              scrollIntoView={this.props.scrollIntoView}
            >
              <EmploymentStatus
                attribute={`${attribute}.Status`}
                name="Status"
                {...this.props.Status}
                onUpdate={this.updateStatus}
                onError={this.props.onError}
                required={this.props.required}
              />
            </Field>

            {this.props?.HasAssociatedCompany?.value === 'Yes' && (
              <>
                <Field
                  attribute={`${attribute}.Telephone`}
                  title={i18n.t(`history.pvq.employment.selfemployment.heading.telephone`)}
                  titleSize="h4"
                  className="override-required"
                  adjustFor="telephone"
                  scrollIntoView={this.props.scrollIntoView}
                >
                  <Telephone
                    attribute={`${attribute}.Telephone`}
                    name="Telephone"
                    {...this.props.Telephone}
                    onUpdate={this.updateTelephone}
                    allowNotApplicable={false}
                    onError={this.props.onError}
                    required={this.props.required}
                    showTimeOfDay
                    showNumberType
                    timeOfDayLabel={i18n.t('identification.pvq.contacts.collection.phoneNumbers.timeOfDayLabel')}
                  />
                </Field>
                <Field
                  attribute={`${attribute}.SelfEmployedEmail`}
                  title={i18n.t(`history.pvq.employment.selfemployment.heading.selfEmployedEmail`)}
                  titleSize="h4"
                  adjustFor="labels"
                  scrollIntoView={this.props.scrollIntoView}
                >
                  <NotApplicable
                    name="SelfEmployedEmailNotApplicable"
                    {...this.props.SelfEmployedEmailNotApplicable}
                    label={i18n.t('history.pvq.employment.selfemployment.notApplicable')}
                    or={i18n.m('reference.para.or')}
                    onUpdate={this.updateSelfEmployedEmailNotApplicable}
                    attribute={`${attribute}.SelfEmployedEmailNotApplicable`}
                  >
                    <Text
                      attribute={`${attribute}.SelfEmployedEmail`}
                      name="SelfEmployedEmail"
                      {...this.props.SelfEmployedEmail}
                      onUpdate={this.updateSelfEmployedEmail}
                      className="text SelfEmployedEmail"
                      onError={this.props.onError}
                      required={this.props.required}
                    />
                  </NotApplicable>
                </Field>
                <Field
                  attribute={`${attribute}.SelfEmployedWebsite`}
                  title={i18n.t(`history.pvq.employment.selfemployment.heading.selfEmployedWebsite`)}
                  titleSize="h4"
                  adjustFor="labels"
                  scrollIntoView={this.props.scrollIntoView}
                >
                  <NotApplicable
                    name="SelfEmployedWebsiteNotApplicable"
                    {...this.props.SelfEmployedWebsiteNotApplicable}
                    label={i18n.t('history.pvq.employment.selfemployment.notApplicable')}
                    or={i18n.m('reference.para.or')}
                    onUpdate={this.updateSelfEmployedWebsiteNotApplicable}
                    attribute={`${attribute}.SelfEmployedWebsiteNotApplicable`}
                  >
                    <Text
                      attribute={`${attribute}.SelfEmployedWebsite`}
                      name="SelfEmployedWebsite"
                      {...this.props.SelfEmployedWebsite}
                      onUpdate={this.updateSelfEmployedWebsite}
                      className="text SelfEmployedWebsite"
                      onError={this.props.onError}
                      required={this.props.required}
                    />
                  </NotApplicable>
                </Field>

                <Field
                  attribute={`${attribute}.IsHomeCompany`}
                  className="IsHomeCompany"
                  required={this.props.required}
                  scrollIntoView={this.props.scrollIntoView}
                >
                  <Branch
                    {...this.props.IsHomeCompany}
                    attribute={`${attribute}.IsHomeCompany`}
                    name="IsHomeCompany"
                    label={i18n.t(`history.pvq.employment.selfemployment.heading.isHomeCompany`)}
                    labelSize="h4"
                    className="sIsHomeCompany"
                    onUpdate={this.updateIsHomeCompany}
                    required={this.props.required}
                    scrollIntoView={this.props.scrollIntoView}
                    onError={this.props.onError}
                  />
                </Field>
                {this.props?.IsHomeCompany?.value === 'No'
                  && (
                    <Field
                      attribute={`${attribute}.Address`}
                      optional
                      onError={this.props.onError}
                      adjustFor="address"
                      shrink
                      scrollIntoView={this.props.scrollIntoView}
                    >
                      <LocationUnknown
                        attribute={`${attribute}.Address`}
                        name="Address"
                        {...this.props.Address}
                        label={i18n.t('history.pvq.employment.selfemployment.heading.address.label')}
                        domesticTitle={i18n.t('history.pvq.employment.selfemployment.heading.address.domesticTitle')}
                        foreignTitle={i18n.t('history.pvq.employment.selfemployment.heading.address.foreignTitle')}
                        includeMilitaryStates
                        layout={LocationUnknown.ADDRESS}
                        geocode
                        addressBook="Employment"
                        addressBooks={this.props.addressBooks}
                        dispatch={this.props.dispatch}
                        onUpdate={this.updateAddress}
                        onError={this.props.onError}
                        required={this.props.required}
                        isPoBoxAllowed={false}
                        showMilitaryInstallation
                        showInternationalStreet
                        showApoFpoDpoPostcodeWhenForeign
                        hideInstallationNameUnknownExplanation
                        hideInstallationNameUnknownWhenForeign
                      />
                    </Field>
                  )}
                <Field
                  attribute={`${attribute}.HasDifferentPhysicalWorkAddress`}
                  className="explanation-left"
                  required={this.props.required}
                  scrollIntoView={this.props.scrollIntoView}
                >
                  <Branch
                    {...this.props.HasDifferentPhysicalWorkAddress}
                    attribute={`${attribute}.HasDifferentPhysicalWorkAddress`}
                    name="HasDifferentPhysicalWorkAddress"
                    label={i18n.t(`history.pvq.employment.selfemployment.heading.hasDifferentPhysicalWorkAddress`)}
                    labelSize="h4"
                    className="employment-departure-branch"

                    onUpdate={this.updateHasDifferentPhysicalWorkAddress}
                    required={this.props.required}
                    scrollIntoView={this.props.scrollIntoView}
                    onError={this.props.onError}
                  />
                </Field>
                <Show when={this.props?.HasDifferentPhysicalWorkAddress?.value === 'Yes'}>
                  <Field
                    attribute={`${attribute}.PhysicalWorkAddress`}
                    optional
                    onError={this.props.onError}
                    adjustFor="address"
                    shrink
                    scrollIntoView={this.props.scrollIntoView}
                  >
                    <LocationUnknown
                      attribute={`${attribute}.PhysicalWorkAddress`}
                      name="PhysicalWorkAddress"
                      {...this.props.PhysicalWorkAddress}
                      label={i18n.t(`${pvqPrefix}.heading.physicalAddress`)}
                      domesticTitle={i18n.t('history.pvq.employment.selfemployment.heading.physicalWorkAddress.domesticTitle')}
                      foreignTitle={i18n.t('history.pvq.employment.selfemployment.heading.physicalWorkAddress.foreignTitle')}
                      includeMilitaryStates
                      layout={LocationUnknown.ADDRESS}
                      geocode
                      addressBook="Employment"
                      addressBooks={this.props.addressBooks}
                      dispatch={this.props.dispatch}
                      onUpdate={this.updatePhysicalWorkAddress}
                      onError={this.props.onError}
                      required={this.props.required}
                      isPoBoxAllowed={false}
                      showMilitaryInstallation
                      showInternationalStreet
                      showApoFpoDpoPostcodeWhenForeign
                      hideInstallationNameUnknownExplanation
                      hideInstallationNameUnknownWhenForeign
                    />
                  </Field>
                </Show>
              </>
            )}
            <Verifier
              attribute={`${attribute}.Supervisor`}
              nonMilitary
              name="Supervisor"
              {...this.props.Supervisor}
              addressBooks={this.props.addressBooks}
              dispatch={this.props.dispatch}
              onUpdate={this.updateSupervisor}
              onError={this.props.onError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              formType={this.props.formType}
              updateSupervisorComments={this.updateSupervisorComments}
              Comments={this.props.Supervisor.Comments}
              requireEmploymentHistorySupervisorEmail={
                this.props.requireEmploymentHistorySupervisorEmail
              }
              requireSupervisorHasDifferentAddress={this.props.requireSupervisorHasDifferentAddress}
              requireSupervisorAddress={this.props.requireSupervisorAddress}
              titles={selfEmploytTitles}
            />
          </>
        )}
        {isUnemployment && (
          <>
            <Field
              attribute={`${attribute}.Dates`}
              title={i18n.t('history.pvq.unemploymentTitles.when')}
              titleSize="h4"
              help="history.employment.default.datesEmployed.help"
              adjustFor="daterange"
              shrink
              scrollIntoView={this.props.scrollIntoView}
            >
              <DateRange
                attribute={`${attribute}.Dates`}
                name="Dates"
                {...this.props.Dates}
                minDateEqualTo
                onUpdate={this.updateDates}
                onError={this.props.onError}
                required={this.props.required}
                hideFromDay
                hideToDay
              />
            </Field>
            <Verifier
              attribute={`${attribute}.Supervisor`}
              nonMilitary
              name="Supervisor"
              {...this.props.Supervisor}
              addressBooks={this.props.addressBooks}
              dispatch={this.props.dispatch}
              onUpdate={this.updateSupervisor}
              onError={this.props.onError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              formType={this.props.formType}
              updateSupervisorComments={this.updateSupervisorComments}
              Comments={this.props.Supervisor.Comments}
              requireEmploymentHistorySupervisorEmail={
                this.props.requireEmploymentHistorySupervisorEmail
              }
              requireSupervisorHasDifferentAddress={this.props.requireSupervisorHasDifferentAddress}
              requireSupervisorAddress={this.props.requireSupervisorAddress}
              titles={unEmployTitles}
            />
          </>
        )}
      </div>
      // #endregion PVQView
    ) : (
      // #region SFView
      <div>
        <EmploymentActivity
          attribute={`${attribute}.EmploymentActivity`}
          name="EmploymentActivity"
          {...this.props.EmploymentActivity}
          onUpdate={this.updateEmploymentActivity}
          onError={this.props.onError}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          requireEmploymentItemNonGovernment={this.props.requireEmploymentItemNonGovernment}
        />

        <Show when={this.showEmployer()}>
          <Field
            attribute={`${attribute}.Employer`}
            title={i18n.t(`${prefix}.heading.employer`)}
            titleSize="h4"
            scrollIntoView={this.props.scrollIntoView}
          > {
              (activity === 'OtherFederal')
                ? (
                  <FederalAgencyAutoComplete
                    name="Employer"
                    className="federal-agency-auto"
                    attribute={`${attribute}.Employer`}
                    onUpdate={this.updateEmployment}
                    onError={this.props.onError}
                    required={this.props.required}
                    value={this.props.Employer?.value}
                    warningMessage={i18n.t('history.federal.agencyDropdownWarning')}
                    onSuggestionSelected={this.onFederalAgencySuggestionSelected}
                  />
                )
                : (
                  <Text
                    attribute={`${attribute}.Employer`}
                    name="Employer"
                    {...this.props.Employer}
                    onUpdate={this.updateEmployment}
                    onError={this.props.onError}
                    className="text full-width employment"
                    required={this.props.required}
                  />
                )
            }
          </Field>
        </Show>

        <Show when={this.showEmployed()}>
          <Field
            attribute={`${attribute}.Title`}
            title={i18n.t(`${prefix}.heading.title`)}
            titleSize="h4"
            adjustFor="labels"
            scrollIntoView={this.props.scrollIntoView}
          >
            <Text
              attribute={`${attribute}.Title`}
              name="Title"
              {...this.props.Title}
              onUpdate={this.updateTitle}
              className="text employment-title"
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
        </Show>

        <Show when={this.showAssignedDuty()}>
          <Field
            attribute={`${attribute}.DutyStation`}
            title={i18n.t(`${prefix}.heading.dutyStation`)}
            titleSize="h4"
            adjustFor="labels"
            scrollIntoView={this.props.scrollIntoView}
          >
            <Text
              attribute={`${attribute}.DutyStation`}
              name="DutyStation"
              {...this.props.DutyStation}
              onUpdate={this.updateDutyStation}
              className="text full-width employment-duty-station"
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
        </Show>

        <Show when={this.showStatus()}>
          <Field
            attribute={`${attribute}.Status`}
            title={i18n.t(`${prefix}.heading.status`)}
            titleSize="h4"
            shrink
            scrollIntoView={this.props.scrollIntoView}
          >
            <EmploymentStatus
              attribute={`${attribute}.Status`}
              name="Status"
              {...this.props.Status}
              onUpdate={this.updateStatus}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
        </Show>

        <Field
          attribute={`${attribute}.Dates`}
          title={i18n.t('history.employment.default.heading.datesEmployed')}
          titleSize="h4"
          help="history.employment.default.datesEmployed.help"
          adjustFor="daterange"
          shrink
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateRange
            attribute={`${attribute}.Dates`}
            name="Dates"
            {...this.props.Dates}
            minDateEqualTo
            onUpdate={this.updateDates}
            onError={this.props.onError}
            required={this.props.required}
            hideFromDay
            hideToDay
          />
        </Field>
        <Comments
          title={i18n.t('history.employment.label.employmentActivityComments')}
          name="EmploymentActivityComments"
          className="optional-comments"
          attribute="EmploymentActivityComments"
          {...this.props.EmploymentActivityComments}
          onUpdate={this.updateEmploymentActivityComments}
          required={false}
        />

        <Show when={this.showEmployed()}>
          <Field
            attribute={`${attribute}.Address`}
            title={i18n.t(`${prefix}.heading.address`)}
            titleSize="h4"
            optional
            help={`${prefix}.address.help`}
            adjustFor="address"
            shrink
            dataTestId="employmentAddress"
            scrollIntoView={this.props.scrollIntoView}
          >
            <Location
              attribute={`${attribute}.Address`}
              name="Address"
              {...this.props.Address}
              layout={Location.ADDRESS}
              geocode
              addressBooks={this.props.addressBooks}
              addressBook="Employment"
              showPostOffice={activity !== 'SelfEmployment'}
              dispatch={this.props.dispatch}
              onUpdate={this.updateAddress}
              onError={this.props.onError}
              label={i18n.t(`${prefix}.address.label`)}
              required={this.props.required}
            />
          </Field>
        </Show>

        {this.showEmployed() && (
          <Field
            attribute={`${attribute}.Telephone`}
            title={i18n.t(`${prefix}.heading.telephone`)}
            titleSize="h4"
            className="override-required"
            adjustFor="telephone"
            scrollIntoView={this.props.scrollIntoView}
          >
            <Telephone
              attribute={`${attribute}.Telephone`}
              name="Telephone"
              {...this.props.Telephone}
              onUpdate={this.updateTelephone}
              allowNotApplicable={false}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
        )}

        {this.showAssignedDuty() && (
          <Comments
            title={i18n.t('history.employment.label.militaryEmploymentComments')}
            name="MilitaryEmploymentComments"
            className="optional-comments"
            attribute="MilitaryEmploymentComments"
            {...this.props.MilitaryEmploymentComments}
            onUpdate={this.updateMilitaryEmploymentComments}
            required={false}
          />
        )}

        {showAdditionalActivity && (
          <Comments
            title={i18n.t('history.employment.label.nonmilitaryEmploymentComments')}
            name="NonMilitaryEmploymentComments"
            className="optional-comments"
            attribute="NonMilitaryEmploymentComments"
            {...this.props.NonMilitaryEmploymentComments}
            onUpdate={this.updateNonMilitaryEmploymentComments}
            required={false}
          />
        )}

        {this.showSelfEmployment() && (
          <Comments
            title={i18n.t('history.employment.label.selfEmploymentComments')}
            name="SelfEmploymentComments"
            className="optional-comment"
            attribute="SelfEmploymentComments"
            {...this.props.SelfEmploymentComments}
            onUpdate={this.updateSelfEmploymentComments}
            required={false}
          />

        )}

        {this.showPhysicalAddress() && (
          <div>
            <PhysicalAddress
              attribute={`${attribute}.PhysicalAddress`}
              name="PhysicalAddress"
              {...this.props.PhysicalAddress}
              title={i18n.t(`${prefix}.heading.physicalAddress`)}
              addressBooks={this.props.addressBooks}
              dispatch={this.props.dispatch}
              onUpdate={this.updatePhysicalAddress}
              onError={this.props.onError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              PhysicalAddressComments={this.props.PhysicalAddressComments}
              PhysicalAddressAnswerComments={this.props.PhysicalAddressAnswerComments}
              EmploymentAddress={this.props.Address}
              updatePhysicalAddressComments={this.updatePhysicalAddressComments}
              updatePhysicalAddressAnswerComments={this.updatePhysicalAddressAnswerComments}
            />
            {hasDifferentPhysicalAddress && (
              <ConnectedAlternateAddress
                attribute={`${attribute}.PhysicalAlternateAddress`}
                address={this.props.PhysicalAlternateAddress}
                addressBook="Employment"
                belongingTo="PhysicalAlternateAddress"
                country={this.props.PhysicalAddress?.Address?.country}
                onUpdate={this.update}
                PhysicalLocationComments={this.props.AddressPhysicalComments}
                AddressComments={this.props.AddressComments}
                AnswerComments={this.props.AddressAnswerComments}
                updatePhysicalLocationComments={this.updateAddressPhysicalComments}
                updateAddressComments={this.updateAddressComments}
                updateAnswerComments={this.updateAddressAnswerComments}
              />
            )}
          </div>
        )}

        {this.showEmployed() && !hasDifferentPhysicalAddress && (
          <ConnectedAlternateAddress
            attribute={`${attribute}.AlternateAddress`}
            address={this.props.AlternateAddress}
            addressBook="Employment"
            belongingTo="AlternateAddress"
            country={this.props.Address?.country}
            onUpdate={this.update}
          />
        )}

        <Show when={this.showSupervisor()}>
          <Supervisor
            attribute={`${attribute}.Supervisor`}
            nonMilitary={showAdditionalActivity}
            name="Supervisor"
            {...this.props.Supervisor}
            addressBooks={this.props.addressBooks}
            dispatch={this.props.dispatch}
            onUpdate={this.updateSupervisor}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            formType={this.props.formType}
            updateSupervisorComments={this.updateSupervisorComments}
            Comments={this.props.Supervisor.Comments}
            requireEmploymentHistorySupervisorEmail={
              this.props.requireEmploymentHistorySupervisorEmail
            }
            requireSupervisorHasDifferentAddress={this.props.requireSupervisorHasDifferentAddress}
            requireSupervisorAddress={this.props.requireSupervisorAddress}
          />
          <ConnectedAlternateAddress
            attribute={`${attribute}.SupervisorAlternateAddress`}
            address={this.props.SupervisorAlternateAddress}
            belongingTo="SupervisorAlternateAddress"
            country={this.props.Supervisor?.Address?.country}
            militaryAddressLabel={i18n.t('address.militaryAddress.supervisor')}
            onUpdate={this.update}
            PhysicalLocationComments={this.props.SupervisorAlternateAddressPhysicalComments}
            AddressComments={this.props.SupervisorAlternateAddressComments}
            AnswerComments={this.props.SupervisorAlternateAddressAnswerComments}
            updatePhysicalLocationComments={this.updateSupervisorAlternateAddressPhysicalComments}
            updateAddressComments={this.updateSupervisorAlternateAddressComments}
            updateAnswerComments={this.updateSupervisorAlternateAddressAnswerComments}
          />
        </Show>

        <Show when={this.showReference()}>
          <div className="reference">
            <Field
              title={i18n.t(`${prefix}.heading.reference`)}
              titleSize="h4"
              optional
              filterErrors={Name.requiredErrorsOnly}
              scrollIntoView={this.props.scrollIntoView}
            >
              <Name
                attribute={`${attribute}.ReferenceName`}
                name="ReferenceName"
                aria-label="Provide a reference"
                prefix="name"
                className="reference-name"
                {...this.props.ReferenceName}
                scrollIntoView={this.props.scrollIntoView}
                onUpdate={this.updateReferenceName}
                onError={this.handleNameError}
                required={this.props.required}
                hideMiddleName
              />
            </Field>

            <Field
              attribute={`${attribute}.ReferencePhone`}
              title={i18n.t('reference.heading.phone.default')}
              titleSize="h4"
              className="override-required"
              help="reference.help.phone"
              adjustFor="telephone"
              scrollIntoView={this.props.scrollIntoView}
            >
              <Telephone
                attribute={`${attribute}.ReferencePhone`}
                name="ReferencePhone"
                className="reference-phone"
                {...this.props.ReferencePhone}
                allowNotApplicable={false}
                onUpdate={this.updateReferencePhone}
                onError={this.props.onError}
                required={this.props.required}
              />
            </Field>

            <Field
              attribute={`${attribute}.ReferenceAddress`}
              title={i18n.t('reference.heading.employment.address')}
              titleSize="h4"
              optional
              help="reference.help.address"
              adjustFor="address"
              scrollIntoView={this.props.scrollIntoView}
            >
              <p>{i18n.t('reference.para.address')}</p>
              <Location
                attribute={`${attribute}.ReferenceAddress`}
                name="ReferenceAddress"
                className="reference-address"
                {...this.props.ReferenceAddress}
                label={i18n.t('reference.label.employment.address')}
                layout={Location.ADDRESS}
                geocode
                addressBooks={this.props.addressBooks}
                addressBook="Reference"
                showPostOffice
                dispatch={this.props.dispatch}
                onUpdate={this.updateReferenceAddress}
                onError={this.props.onError}
              />
            </Field>
            {this.showSelfEmployment() && (
              <Comments
                title={i18n.t('history.employment.label.referenceLocationComments')}
                name="ReferenceComments"
                className="optional-comment"
                attribute="ReferenceComments"
                {...this.props.ReferenceComments}
                onUpdate={this.updateReferenceComments}
                required={false}
              />
            )}
            {this.showUnemployment() && (
              <Comments
                title={i18n.t('history.employment.label.unemploymentComments')}
                name="UnemploymentComments"
                className="optional-comment"
                attribute="UnemploymentComments"
                {...this.props.UnemploymentComments}
                onUpdate={this.updateUnemploymentComments}
                required={false}
              />
            )}
            <ConnectedAlternateAddress
              attribute={`${attribute}.ReferenceAlternateAddress`}
              address={this.props.ReferenceAlternateAddress}
              addressBook="Reference"
              belongingTo="ReferenceAlternateAddress"
              country={this.props.ReferenceAddress?.country}
              militaryAddressLabel={i18n.t(`${prefix}.heading.militaryAddress`)}
              onUpdate={this.update}
              PhysicalLocationComments={this.props.ReferenceAlternateAddressPhysicalComments}
              AddressComments={this.props.ReferenceAlternateAddressComments}
              AnswerComments={this.props.ReferenceAlternateAddressAnswerComments}
              updatePhysicalLocationComments={this.updateReferenceAlternateAddressPhysicalComments}
              updateAddressComments={this.updateReferenceAlternateAddressComments}
              updateAnswerComments={this.updateReferenceAlternateAddressAnswerComments}
            />
          </div>
        </Show>

        <Show when={showAdditionalActivity}>
          <Field
            attribute={`${attribute}.AdditionalTitle`}
            title={i18n.t(`${prefix}.heading.additionalActivity`)}
            titleSize="h3"
            optional
            className="no-margin-bottom"
            scrollIntoView={this.props.scrollIntoView}
          >
            {i18n.m(`${prefix}.para.additionalActivity`)}
          </Field>

          <AdditionalActivity
            attribute={`${attribute}.Additional`}
            name="Additional"
            {...this.props.Additional}
            employmentFromDate={this.props.Dates.from}
            onUpdate={this.updateAdditional}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          />
        </Show>

        <Show
          when={
            this.showEmployed()
            && this.props.requireEmploymentItemLeft
          }
        >
          <ReasonLeft
            attribute={`${attribute}.ReasonLeft`}
            name="ReasonLeft"
            recordYears={recordYears}
            {...this.props.ReasonLeft}
            Dates={this.props.Dates}
            onUpdate={this.updateReasonLeft}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            ReasonLeftComments={this.props.ReasonLeftComments}
            ReasonLeftAnswerComments={this.props.ReasonLeftAnswerComments}
            updateReasonLeftComments={this.updateReasonLeftComments}
            updateReasonLeftAnswerComments={this.updateReasonLeftAnswerComments}
            formType={this.props.formType}
          />
        </Show>

        <Show
          when={
            this.showEmployerPvq()
            && this.props.requireEmploymentItemLeft
          }
        >
          <Reprimand
            attribute={`${attribute}.Reprimand`}
            name="Reprimand"
            recordYears={recordYears}
            {...this.props.Reprimand}
            Dates={this.props.Dates}
            onUpdate={this.updateReprimand}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            ReprimandAnswerComments={this.props.ReprimandAnswerComments}
            ReprimandComments={this.props.ReprimandComments}
            updateReprimandComments={this.updateReprimandComments}
            updateReprimandAnswerComments={this.updateReprimandAnswerComments}
          />
        </Show>
      </div>
      // #endregion SFView
    );
  }
}

EmploymentItem.defaultProps = {
  EmploymentActivity: {},
  Employer: {},
  Dates: {},
  Title: {},
  EmploymentActivityComments: {},
  MilitaryEmploymentComments: {},
  NonMilitaryEmploymentComments: {},
  Comments: {},
  SupervisorAlternateAddressPhysicalComments: {},
  SupervisorAlternateAddressAnswerComments: {},
  SupervisorAlternateAddressComments: {},
  SelfEmploymentComments: {},
  AddressPhysicalComments: {},
  AddressAnswerComments: {},
  AddressComments: {},
  ReprimandComments: {},
  ReprimandAnswerComments: {},
  ReasonLeftComments: {},
  ReasonLeftAnswerComments: {},
  ReferenceComments: {},
  ReferenceAlternateAddressPhysicalComments: {},
  ReferenceAlternateAddressAnswerComments: {},
  ReferenceAlternateAddressComments: {},
  UnemploymentComments: {},
  PhysicalAddressComments: {},
  PhysicalAddressAnswerComments: {},
  DutyStation: {},
  Status: {},
  Address: {},
  Telephone: {},
  Supervisor: { Address: {} },
  ReferenceName: {},
  ReferencePhone: {},
  ReferenceAddress: {},
  PhysicalAddress: {},
  Additional: {},
  ReasonLeft: {},
  Reprimand: {},
  addressBooks: {},
  recordYears: 7,
  IsInternship: {},
  MilitaryBranch: {},
  Assignments: {},
  WasFired: {},
  FiredDetails: {},
  WasLeaveBeforeFired: {},
  LeaveBeforeFiredDetails: {},
  WasLeaveAfterNoticeMisconduct: {},
  LeaveAfterNoticeDetails: {},
  WasLeaveAfterNoticePerformance: {},
  WasLeaveAfterNoticePerformanceDetails: {},
  WasLeavePendingOutcome: {},
  WasLeavePendingOutcomeDetails: {},
  StillInBusiness: {},
  ReturnedToWork: {},
  ReturnDate: {},
  HasAssociatedCompany: {},
  SelfEmployedEmail: {},
  SelfEmployedEmailNotApplicable: { applicable: true },
  SelfEmployedWebsite: {},
  SelfEmployedWebsiteNotApplicable: { applicable: true },
  IsHomeCompany: {},
  dispatch: () => { },
  onUpdate: () => { },
  onError: (value, arr) => arr,
};
